/**
 * DEV-258 — cliente do gateway público do BFF (`/public/alignment/*`).
 * Não usa o `client-api-sdk`: aqui 401 é sessão expirada (volta ao
 * Onboarding), nunca login do Keycloak (`research.md` D16).
 */
import { recruiterEnv } from '@/config/env';
import type { FormDefinition, JobDescription } from './definition';

export interface AnswerIssue {
  page: string;
  question: string;
  reason: string;
  message: string;
}

export class AlignmentApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: AnswerIssue[];
  readonly questions: string[];
  readonly retryAfterSeconds: number | null;

  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body.error === 'string' ? body.error : `HTTP ${status}`);
    this.name = 'AlignmentApiError';
    this.status = status;
    this.code = typeof body.code === 'string' ? body.code : status === 0 ? 'network' : 'unknown';
    this.issues = Array.isArray(body.issues) ? (body.issues as AnswerIssue[]) : [];
    this.questions = Array.isArray(body.questions) ? (body.questions as string[]) : [];
    this.retryAfterSeconds =
      typeof body.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null;
  }
}

const SESSION_HEADER = 'x-alignment-session';
const TIMEOUT_MS = 20_000;

async function request<T>(
  path: string,
  init: { method?: string; session?: string; body?: unknown; signal?: AbortSignal } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  if (init.session) headers[SESSION_HEADER] = init.session;
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  const signal = init.signal
    ? AbortSignal.any([init.signal, AbortSignal.timeout(TIMEOUT_MS)])
    : AbortSignal.timeout(TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${recruiterEnv.apiUrl.replace(/\/+$/, '')}${path}`, {
      method: init.method ?? 'GET',
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal,
    });
  } catch (err) {
    if (init.signal?.aborted) throw err;
    const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
    throw new AlignmentApiError(0, timedOut ? { code: 'timeout' } : {});
  }
  const text = await res.text().catch(() => '');
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  if (!res.ok) {
    throw new AlignmentApiError(
      res.status,
      (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
    );
  }
  return body as T;
}

export interface AlignmentAccess {
  programName: string;
  programLogoUrl: string | null;
}

export interface OpenedSession {
  sessionToken: string;
  jobDescriptionId: string;
  expiresAt: string;
}

export type CatalogueName =
  | 'states'
  | 'cities'
  | 'course-areas'
  | 'course-groups'
  | 'benefits'
  | 'additional-payments'
  | 'disability-types';

const enc = encodeURIComponent;

export const alignmentApi = {
  getAccess: (tenant: string, formId: string, signal?: AbortSignal) =>
    request<AlignmentAccess>(`/public/alignment/${enc(tenant)}/${enc(formId)}`, { signal }),

  openSession: (
    tenant: string,
    formId: string,
    body: { token: string; name: string; email: string }
  ) =>
    request<OpenedSession>(`/public/alignment/${enc(tenant)}/${enc(formId)}/sessions`, {
      method: 'POST',
      body,
    }),

  getDescription: (session: string, signal?: AbortSignal) =>
    request<JobDescription>('/public/alignment/session', { session, signal }),

  getDefinition: (session: string, signal?: AbortSignal) =>
    request<FormDefinition>('/public/alignment/session/form-definition', { session, signal }),

  savePage: (session: string, pageName: string, answers: Record<string, unknown>) =>
    request<{ updatedAt: string }>(`/public/alignment/session/pages/${enc(pageName)}`, {
      method: 'PATCH',
      session,
      body: { answers },
    }),

  submit: (session: string) =>
    request<{ submittedAt: string }>('/public/alignment/session/submit', {
      method: 'POST',
      session,
    }),

  getCatalogue: <T = unknown>(
    session: string,
    name: CatalogueName,
    params: Record<string, string | undefined> = {},
    signal?: AbortSignal
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
    const query = qs.toString();
    return request<{ items: T[] }>(
      `/public/alignment/session/catalogues/${name}${query ? `?${query}` : ''}`,
      { session, signal }
    );
  },
};
